import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const STARTERS = [
  'Was sind meine wichtigsten Dokumente?',
  'Zeige alle Dokumente als Tabelle',
  'Welche Dokumente haben Tags?',
  'Erstelle eine Zusammenfassung meiner Dokumente',
  'Finde Informationen ueber Kuendigungsfristen',
  'Welche PDFs habe ich hochgeladen?',
];

function TablePanel({ data }) {
  if (!data?.columns || !data?.rows) return null;
  return (
    <div style={{ overflow: 'auto' }}>
      {data.title && <h4 style={{ color: 'var(--text-primary)', marginBottom: 8 }}>{data.title}</h4>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{data.columns.map((c, i) => <th key={i} style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid var(--border)', color: '#bb9af7', fontSize: 'var(--font-size-xs)', fontWeight: 600, textTransform: 'uppercase' }}>{c}</th>)}</tr></thead>
        <tbody>{data.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)' }}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function ReportPanel({ data }) {
  if (!data?.sections) return null;
  return (
    <div>
      {data.title && <h3 style={{ color: 'var(--text-primary)', marginBottom: 12, fontSize: 'var(--font-size-lg)' }}>{data.title}</h3>}
      {data.sections.map((s, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <h4 style={{ color: '#bb9af7', marginBottom: 6, fontSize: 'var(--font-size-base)' }}>{s.heading || s.title}</h4>
          <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{s.content || s.text}</div>
        </div>
      ))}
    </div>
  );
}

function SummaryPanel({ data }) {
  if (!data) return null;
  return (
    <div>
      {data.doc_title && <h3 style={{ color: 'var(--text-primary)', marginBottom: 8 }}>{data.doc_title}</h3>}
      <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data.summary}</div>
    </div>
  );
}

export default function Agent() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeOutput, setActiveOutput] = useState(null);
  const endRef = useRef(null);
  const nav = useNavigate();

  const send = useCallback(async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const r = await fetch('/api/agent/structured', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history }),
      });
      const d = await r.json();
      const am = { role: 'assistant', content: d.message, output_type: d.output_type, output_data: d.output_data, actions: d.suggested_actions };
      setMessages(prev => [...prev, am]);
      if (d.output_data) setActiveOutput(am);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Fehler: ' + e.message }]);
    }
    setLoading(false);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [input, loading, messages]);

  const handleAction = (a) => {
    if (a.type === 'open_doc') nav(`/library/${a.payload}`);
    else if (a.type === 'search') nav(`/search?q=${a.payload}`);
    else if (a.type === 'generate') nav('/generate');
    else send(a.label);
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left - Chat */}
      <div style={{ flex: 2, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border)' }}>
        <div style={{ padding: '12px var(--gap-md)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>KI Agent</span>
          <span style={{ background: 'rgba(187,154,247,0.15)', color: '#bb9af7', borderRadius: 4, padding: '2px 8px', fontSize: 11 }}>Claude Haiku</span>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--gap-md)' }}>
          {messages.length === 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 40 }}>
              {STARTERS.map(s => (
                <button key={s} onClick={() => send(s)} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius-lg)', padding: '10px 16px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', maxWidth: 240 }}>{s}</button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
              <div style={{ maxWidth: '80%', background: m.role === 'user' ? 'rgba(187,154,247,0.15)' : 'var(--bg-secondary)', border: `1px solid ${m.role === 'user' ? 'rgba(187,154,247,0.3)' : 'var(--border)'}`, borderRadius: 'var(--border-radius)', padding: '10px 14px' }}>
                <div style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap' }}>{m.content}</div>
                {m.output_data && (
                  <button onClick={() => setActiveOutput(m)} style={{ marginTop: 6, background: 'rgba(187,154,247,0.1)', border: '1px solid rgba(187,154,247,0.2)', borderRadius: 4, padding: '4px 10px', color: '#bb9af7', cursor: 'pointer', fontSize: 11 }}>
                    {m.output_type === 'table' ? 'Tabelle' : m.output_type === 'report' ? 'Bericht' : 'Zusammenfassung'} anzeigen
                  </button>
                )}
                {m.actions?.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                    {m.actions.map((a, j) => <button key={j} onClick={() => handleAction(a)} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 4, padding: '4px 10px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}>{a.label}</button>)}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && <div style={{ textAlign: 'center', color: '#bb9af7', fontSize: 'var(--font-size-sm)' }}>Denkt nach...</div>}
          <div ref={endRef} />
        </div>
        <div style={{ padding: 'var(--gap-md)', borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send(); }} placeholder="Frage stellen..." style={{ flex: 1, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', outline: 'none' }} />
          <button onClick={() => send()} disabled={loading} style={{ background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '10px 16px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer' }}>{'\u2191'}</button>
        </div>
      </div>

      {/* Right - Output */}
      <div style={{ flex: 3, overflow: 'auto', padding: 'var(--gap-lg)' }}>
        {activeOutput ? (
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius-lg)', padding: 24 }}>
            {activeOutput.output_type === 'table' && <TablePanel data={activeOutput.output_data} />}
            {activeOutput.output_type === 'report' && <ReportPanel data={activeOutput.output_data} />}
            {activeOutput.output_type === 'summary' && <SummaryPanel data={activeOutput.output_data} />}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>Output erscheint hier</div>
        )}
      </div>
    </div>
  );
}
